import { useEffect, useState } from 'react';
import {
  createUser,
  getAccess,
  getUsers,
  patchUser,
  type ManagedUser,
  type Role,
} from './admin-api';
import { PasswordModal } from './dialogs';
import { useUser } from './user-context';

// Управление пользователями: список, добавление, смена роли/пароля, активность.
export function UsersPanel() {
  const me = useUser();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [pwFor, setPwFor] = useState<ManagedUser | null>(null);
  const [form, setForm] = useState({ email: '', name: '', role: '', password: '' });

  const load = () => getUsers().then((r) => setUsers(r.users));
  useEffect(() => {
    load();
    getAccess().then((a) => setRoles(a.roles));
  }, []);

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await createUser(form.email.trim(), form.name.trim(), form.role, form.password);
      setForm({ email: '', name: '', role: '', password: '' });
      setAdding(false);
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Не удалось создать пользователя.');
    } finally {
      setBusy(false);
    }
  }

  async function act(p: Promise<unknown>) {
    setBusy(true);
    setMsg(null);
    try {
      await p;
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Ошибка.');
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(u: ManagedUser, role: string) {
    if (role === u.role_key) return;
    await act(patchUser(u.id, { role }));
  }

  if (!users) return <p className="muted">Загрузка…</p>;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <p className="muted small grow" style={{ margin: 0 }}>
          Смена роли применяется при следующем входе пользователя.
        </p>
        <button className="btn sm" disabled={busy} onClick={() => setAdding((v) => !v)}>
          {adding ? 'Отмена' : '+ Пользователь'}
        </button>
      </div>
      {msg && <p className="err">{msg}</p>}

      {adding && (
        <form className="glass pad" style={{ marginBottom: 14 }} onSubmit={submitNew}>
          <div className="fields">
            <label className="field">
              <span className="muted small">Email</span>
              <input
                type="email"
                value={form.email}
                disabled={busy}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ivanov@azam.kz"
              />
            </label>
            <label className="field">
              <span className="muted small">Имя</span>
              <input
                value={form.name}
                disabled={busy}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Иванов И."
              />
            </label>
            <label className="field">
              <span className="muted small">Роль</span>
              <select
                value={form.role}
                disabled={busy}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="">— выбрать —</option>
                {roles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.ico} {r.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="muted small">Пароль</span>
              <input
                type="password"
                value={form.password}
                disabled={busy}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="не короче 6 символов"
              />
            </label>
          </div>
          <button
            className="btn sm"
            type="submit"
            disabled={busy || !form.email || !form.name || !form.role}
          >
            Создать
          </button>
        </form>
      )}

      <div className="tablewrap glass">
        <table className="tbl card-table">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Роль</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.email.toLowerCase() === me.email.toLowerCase();
              return (
                <tr key={u.id} style={{ cursor: 'default', opacity: u.active ? 1 : 0.5 }}>
                  <td data-label="Пользователь">
                    <b>{u.name}</b>
                    {isSelf && (
                      <span className="chip" style={{ marginLeft: 6 }}>
                        вы
                      </span>
                    )}
                    {u.is_demo === 1 && (
                      <span className="chip vi" style={{ marginLeft: 6 }}>
                        демо
                      </span>
                    )}
                    <div className="rmeta mono">{u.email}</div>
                  </td>
                  <td data-label="Роль">
                    <select
                      value={u.role_key}
                      disabled={busy || isSelf}
                      title={isSelf ? 'Нельзя менять роль своей учётной записи' : undefined}
                      onChange={(e) => changeRole(u, e.target.value)}
                    >
                      {roles.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td data-label="Статус">
                    {u.active ? (
                      <span className="chip grn">активен</span>
                    ) : (
                      <span className="chip red">отключён</span>
                    )}
                  </td>
                  <td className="r">
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn ghost sm" disabled={busy} onClick={() => setPwFor(u)}>
                        Пароль
                      </button>
                      <button
                        className="btn ghost sm"
                        disabled={busy || isSelf}
                        title={isSelf ? 'Нельзя отключить свою учётную запись' : undefined}
                        onClick={() => act(patchUser(u.id, { active: !u.active }))}
                      >
                        {u.active ? 'Отключить' : 'Включить'}
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pwFor && (
        <PasswordModal
          title={`Новый пароль · ${pwFor.name}`}
          onSubmit={(pw) => patchUser(pwFor.id, { password: pw }).then(() => {})}
          onClose={() => setPwFor(null)}
        />
      )}
    </>
  );
}
